// Shared geometry primitives for component placement. Both the deterministic
// layout planner and the appStore placement fallbacks import from here so the
// margin/clamping rules cannot drift between the two code paths.

export type LayoutPoint = { x: number; y: number };
export type LayoutSize = { width: number; height: number };
export type LayoutRect = { x: number; y: number; width: number; height: number };

type MovableComponent = {
  position: LayoutPoint;
  size: LayoutSize;
  score?: number;
};

export function rectsOverlap(a: LayoutRect, b: LayoutRect): boolean {
  return !(
    b.x >= a.x + a.width ||
    b.x + b.width <= a.x ||
    b.y >= a.y + a.height ||
    b.y + b.height <= a.y
  );
}

export function clampPositionToBounds(
  position: LayoutPoint,
  size: LayoutSize,
  screen: LayoutSize,
  margin: number,
): LayoutPoint {
  return {
    x: Math.floor(Math.max(margin, Math.min(screen.width - size.width - margin, position.x))),
    y: Math.floor(Math.max(margin, Math.min(screen.height - size.height - margin, position.y))),
  };
}

export function occupancyRect(position: LayoutPoint, size: LayoutSize, margin: number): LayoutRect {
  return {
    x: position.x - margin,
    y: position.y - margin,
    width: size.width + margin * 2,
    height: size.height + margin * 2,
  };
}

export function movementCost(component: MovableComponent, toPosition: LayoutPoint): number {
  const distance = Math.hypot(
    toPosition.x - component.position.x,
    toPosition.y - component.position.y,
  );

  // Cost factors: distance (farther = higher), score (higher score = costlier
  // to disturb), size (larger components = costlier to move).
  const distanceCost = distance / 100;
  const scoreCost = (component.score || 5) * 2;
  const sizeCost = (component.size.width * component.size.height) / 10000;

  return distanceCost + scoreCost + sizeCost;
}

/**
 * The available position nearest to `preferred` (Euclidean distance). It
 * walks square rings of `step` around the clamped preferred point, clamping
 * out-of-range points onto the bounds for `size` (so the far edges are
 * covered without a top-left bias), and keeps the closest accepted point
 * rather than the first one in ring order. A ring at radius r holds no point closer than r - step (only points
 * clamped onto the bounds come closer than r), so the walk stops once that
 * exceeds the best distance found. `seeds` are extra candidates off the ring
 * lattice, such as a position already clamped for a larger footprint.
 */
export function findNearestRingPosition(
  preferred: LayoutPoint,
  size: LayoutSize,
  screen: LayoutSize,
  margin: number,
  step: number,
  isAvailable: (point: LayoutPoint) => boolean,
  seeds: LayoutPoint[] = [],
): LayoutPoint | null {
  const minX = margin;
  const minY = margin;
  const maxX = screen.width - size.width - margin;
  const maxY = screen.height - size.height - margin;
  if (maxX < minX || maxY < minY || step <= 0) return null;

  const start = clampPositionToBounds(preferred, size, screen, margin);
  // Clamped coordinates are integers well under 2^20, so one number keys a point.
  const seen = new Set<number>();
  let best: LayoutPoint | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  const visit = (x: number, y: number) => {
    const clampedX = Math.floor(Math.max(minX, Math.min(maxX, x)));
    const clampedY = Math.floor(Math.max(minY, Math.min(maxY, y)));
    const key = clampedX * 2_097_152 + clampedY;
    if (seen.has(key)) return;
    seen.add(key);
    const distance = Math.hypot(clampedX - start.x, clampedY - start.y);
    if (distance >= bestDistance) return;
    const point = { x: clampedX, y: clampedY };
    if (!isAvailable(point)) return;
    best = point;
    bestDistance = distance;
  };

  visit(start.x, start.y);
  if (bestDistance === 0) return best;
  for (const seed of seeds) visit(seed.x, seed.y);

  const maxRadius = Math.max(start.x - minX, maxX - start.x, start.y - minY, maxY - start.y);

  for (let radius = step; radius <= maxRadius + step && radius - step <= bestDistance; radius += step) {
    const left = start.x - radius;
    const right = start.x + radius;
    const top = start.y - radius;
    const bottom = start.y + radius;

    for (let x = left; x <= right; x += step) {
      visit(x, top);
      visit(x, bottom);
    }
    for (let y = top + step; y <= bottom - step; y += step) {
      visit(left, y);
      visit(right, y);
    }
  }

  return best;
}

export type FreeRegionAnalysis = {
  regions: LayoutRect[];
  totalFreeArea: number;
};

/**
 * Rasterizes occupied rects into a coarse grid and extracts the free space as
 * maximal free rectangles (largest first, contained candidates filtered out).
 * O(rects × covered cells) to build the grid and a histogram sweep to find
 * rectangles — no per-cell allocations or per-cell component scans.
 */
export function computeFreeRegions(
  occupiedRects: LayoutRect[],
  screen: LayoutSize,
  gridStep: number,
  maxRegions: number = 24,
): FreeRegionAnalysis {
  const gridWidth = Math.ceil(screen.width / gridStep);
  const gridHeight = Math.ceil(screen.height / gridStep);
  if (gridWidth <= 0 || gridHeight <= 0) {
    return { regions: [], totalFreeArea: 0 };
  }

  const occupied: Uint8Array[] = Array.from({ length: gridHeight }, () => new Uint8Array(gridWidth));
  for (const rect of occupiedRects) {
    const startX = Math.max(0, Math.floor(rect.x / gridStep));
    const endX = Math.min(gridWidth - 1, Math.ceil((rect.x + rect.width) / gridStep) - 1);
    const startY = Math.max(0, Math.floor(rect.y / gridStep));
    const endY = Math.min(gridHeight - 1, Math.ceil((rect.y + rect.height) / gridStep) - 1);
    for (let y = startY; y <= endY; y++) {
      occupied[y].fill(1, startX, endX + 1);
    }
  }

  let freeCells = 0;
  const heights = new Int32Array(gridWidth);
  const candidates: LayoutRect[] = [];

  for (let y = 0; y < gridHeight; y++) {
    const row = occupied[y];
    for (let x = 0; x < gridWidth; x++) {
      if (row[x]) {
        heights[x] = 0;
      } else {
        heights[x] += 1;
        freeCells++;
      }
    }

    // Largest-rectangle-in-histogram sweep; every popped bar yields a free
    // rectangle candidate with its bottom edge on this row.
    const stack: Array<{ index: number; height: number }> = [];
    for (let x = 0; x <= gridWidth; x++) {
      const height = x < gridWidth ? heights[x] : 0;
      let startIndex = x;
      while (stack.length > 0 && stack[stack.length - 1].height >= height) {
        const popped = stack.pop()!;
        startIndex = popped.index;
        if (popped.height > 0) {
          candidates.push({
            x: popped.index * gridStep,
            y: (y - popped.height + 1) * gridStep,
            width: (x - popped.index) * gridStep,
            height: popped.height * gridStep,
          });
        }
      }
      if (height > 0 && (stack.length === 0 || stack[stack.length - 1].height < height)) {
        stack.push({ index: startIndex, height });
      }
    }
  }

  candidates.sort((a, b) => b.width * b.height - a.width * a.height);

  const regions: LayoutRect[] = [];
  for (const candidate of candidates) {
    if (regions.length >= maxRegions) break;
    const contained = regions.some((region) =>
      candidate.x >= region.x &&
      candidate.y >= region.y &&
      candidate.x + candidate.width <= region.x + region.width &&
      candidate.y + candidate.height <= region.y + region.height,
    );
    if (!contained) {
      regions.push({
        x: candidate.x,
        y: candidate.y,
        width: Math.min(candidate.width, screen.width - candidate.x),
        height: Math.min(candidate.height, screen.height - candidate.y),
      });
    }
  }

  return { regions, totalFreeArea: freeCells * gridStep * gridStep };
}
